import { useMemo, useState, type FC } from "react";
import type { Dayjs } from "dayjs";

import { PillTabs, type PillTabsItem } from "../../../shared";
import type { AffiliateKpisMetricId } from "../../AffiliateKpis/AffiliateKpisOverview";
import AffiliateProfileKpisPanel from "../AffiliateProfileKpisPanel/AffiliateProfileKpisPanel";
import AffiliatePromoCodes from "../AffiliatePromoCodes/AffiliatePromoCodes";
import AffiliateUsers from "../AffiliateUsers/AffiliateUsers";
import type {
  KpiEligibleId,
  ShareableColumnId,
} from "../../NewAffiliate/AffiliatePamAccount/affiliatePamAccountData";
import { KPI_ELIGIBLE_IDS } from "../../NewAffiliate/AffiliatePamAccount/affiliatePamAccountData";
import { AppStrings } from "../../../../utils/constants/AppStrings";

import styles from "./PortalViewPreview.module.css";
import { getAdminNow } from "../../../../utils/dateService";

// Mirrors the portal's tab set exactly — no Affiliate Info here because
// contract / plan / lifecycle data never reaches the portal.
type PreviewTab = "kpis" | "promoCodes" | "users";

// Widen the literal tuple so `.includes(id)` accepts any ShareableColumnId.
const KPI_IDS: ReadonlyArray<ShareableColumnId> = KPI_ELIGIBLE_IDS;

// `avgLtv` is FE-derived from Total Revenue, so it isn't a toggleable id —
// show the tile only while `totalRevenue` is selected. With Revenue off the
// affiliate can't get LTV either, so the preview shouldn't promise it.
const selectionToVisibleMetrics = (
  selection: ReadonlyArray<ShareableColumnId>,
): ReadonlyArray<AffiliateKpisMetricId> => {
  const metrics: KpiEligibleId[] = selection.filter(
    (id): id is KpiEligibleId => KPI_IDS.includes(id),
  );
  return selection.includes("totalRevenue")
    ? [...metrics, "avgLtv"]
    : metrics;
};

export type PortalViewPreviewProps = {
  // Live toggle selection — drives which KPI tiles / table columns render.
  selectedColumns: ReadonlyArray<ShareableColumnId>;
  // Absent while onboarding (New Affiliate wizard): every tab renders its
  // empty state and no query fires.
  affiliateId?: string;
  // Namespaces tab/panel element ids + testids so two mounted previews
  // (modal + wizard) never collide.
  idPrefix: string;
  ariaLabel?: string;
};

/**
 * Tabbed preview of what the affiliate sees in their portal. Shared by
 * the Edit Portal View modal and the New Affiliate wizard's PAM Account
 * step so the two surfaces can't drift.
 */
const PortalViewPreview: FC<PortalViewPreviewProps> = ({
  selectedColumns,
  affiliateId,
  idPrefix,
  ariaLabel,
}) => {
  const [activeTab, setActiveTab] = useState<PreviewTab>("kpis");

  const tabs = useMemo<ReadonlyArray<PillTabsItem<PreviewTab>>>(
    () =>
      (
        [
          { id: "kpis", label: AppStrings.KPIS },
          { id: "promoCodes", label: AppStrings.PROMO_CODES },
          { id: "users", label: AppStrings.USERS },
        ] as const
      ).map((tab) => ({
        ...tab,
        elementId: `${idPrefix}-tab-${tab.id}`,
        ariaControls: `${idPrefix}-panel-${tab.id}`,
      })),
    [idPrefix],
  );

  const visibleMetrics = useMemo(
    () => selectionToVisibleMetrics(selectedColumns),
    [selectedColumns],
  );

  // No date picker of its own — preview at month-to-date, same default
  // the profile lands on.
  const previewDates = useMemo<[Dayjs, Dayjs]>(
    () => [getAdminNow().startOf("month"), getAdminNow()],
    [],
  );
  const startDate = previewDates[0].format("YYYY-MM-DD");
  const endDate = previewDates[1].format("YYYY-MM-DD");

  return (
    <section
      className={styles.previewColumn}
      data-testid={`${idPrefix}-preview`}
    >
      <PillTabs<PreviewTab>
        className={styles.tabs}
        items={tabs}
        value={activeTab}
        onChange={setActiveTab}
        ariaLabel={ariaLabel ?? AppStrings.EDIT_PORTAL_VIEW}
        data-testid={`${idPrefix}-tabs`}
      />
      <div
        className={styles.tabPanel}
        role="tabpanel"
        id={`${idPrefix}-panel-${activeTab}`}
        aria-labelledby={`${idPrefix}-tab-${activeTab}`}
        data-testid={`${idPrefix}-panel-${activeTab}`}
      >
        {activeTab === "kpis" && (
          <AffiliateProfileKpisPanel
            affiliateId={affiliateId}
            dates={previewDates}
            showFilters={false}
            selectedCode={undefined}
            onCodeChange={() => {}}
            codeOptions={[]}
            isLoadingCodes={false}
            visibleMetrics={visibleMetrics}
          />
        )}
        {activeTab === "promoCodes" && (
          <AffiliatePromoCodes
            affiliateId={affiliateId}
            startDate={startDate}
            endDate={endDate}
            selectedColumns={selectedColumns}
            // The portal is read-only for affiliates — the preview can't
            // offer New Code either.
            readOnly
          />
        )}
        {activeTab === "users" && (
          <AffiliateUsers
            affiliateId={affiliateId}
            selectedColumns={selectedColumns}
            startDate={startDate}
            endDate={endDate}
          />
        )}
      </div>
    </section>
  );
};

export default PortalViewPreview;
